import type {
  DiagnosticProcedureDto,
  ReportResponseDto,
} from '@/api/WhisApi/generated';
import type { FormFieldValue } from '@/screens/ReportCreation/reportCreation.types';
import type { TFunction } from 'i18next';
export { hasMissingRequiredFields } from '@/screens/ReportCreation/utils/missingRequiredFields.utils';

const normalizeSpecieValue = (value: string): string =>
  value.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

const getLocalizedName = (
  names: Record<string, string> | undefined,
  language: string,
): string | undefined => {
  if (!names) {
    return undefined;
  }

  const localized = names[language];
  if (typeof localized === 'string' && localized.trim().length > 0) {
    return localized;
  }

  const fallback = Object.values(names).find(
    (value): value is string =>
      typeof value === 'string' && value.trim().length > 0,
  );

  return fallback;
};

export const resolveOptionLabel = (
  options: { value: string; label: string }[] | undefined,
  value: FormFieldValue,
): string | number | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (!options?.length) {
    return typeof value === 'string' || typeof value === 'number'
      ? value
      : undefined;
  }

  if (typeof value !== 'string' && typeof value !== 'number') {
    return undefined;
  }

  const valueString = String(value);
  const normalizedValue = normalizeSpecieValue(valueString);
  const option =
    options.find((item) => item.value === valueString) ??
    options.find(
      (item) => normalizeSpecieValue(item.value) === normalizedValue,
    );
  return option?.label ?? value;
};

export const formatDateForLocale = (
  value: FormFieldValue,
  locale: string,
): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  const allowedValue =
    value instanceof Date ||
    typeof value === 'string' ||
    typeof value === 'number'
      ? value
      : undefined;

  if (!allowedValue) {
    return undefined;
  }

  const date =
    allowedValue instanceof Date ? allowedValue : new Date(allowedValue);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return date.toLocaleDateString(locale);
};

export const formatBooleanValue = (
  value: FormFieldValue,
  t: TFunction,
): string | undefined => {
  if (typeof value !== 'boolean') {
    return undefined;
  }

  return value
    ? t('reportCreation.step2.options.yesNo.yes')
    : t('reportCreation.step2.options.yesNo.no');
};

export const getReportFieldValue = (
  report: ReportResponseDto,
  fieldId: string,
  language: string,
): FormFieldValue => {
  switch (fieldId) {
    case 'animalType':
      return report.animalType;
    case 'species':
      return report.specie;
    case 'wildtype':
      return report.wildType;
    case 'location.latitude':
      return report.latitude !== undefined
        ? String(report.latitude)
        : undefined;
    case 'location.longitude':
      return report.longitude !== undefined
        ? String(report.longitude)
        : undefined;
    case 'inaccurateLocation':
      return report.inaccurateLocation;
    case 'dateOfDetection':
      return report.detectionDate;
    case 'surveillanceType':
      return report.surveillanceType;
    case 'description':
      return report.description;
    case 'numberOfDeaths':
      return report.numberOfDeaths;
    case 'numberOfSick':
      return report.numberOfSicks;
    case 'populationSize':
      return report.populationSize;
    case 'humanAffected':
      return report.humanAffected;
    case 'domesticAnimalAffected':
      return report.domesticAnimalAffected;
    case 'knowTheCause':
      return report.knownCause === undefined
        ? undefined
        : report.knownCause
          ? 'yes'
          : 'no';
    case 'etiologyType':
      return report.etiologyType?.name?.[language];
    case 'etiologyAgent':
      return report.etiologicalAgent?.name?.[language];
    case 'diagnosticProcedure':
      return report.diagnosticProcedures?.length ? 'yes' : 'no';
    default:
      return undefined;
  }
};

export const getDiagnosticProcedureFieldValue = (
  procedure: DiagnosticProcedureDto,
  fieldId: string,
  language: string,
): FormFieldValue => {
  switch (fieldId) {
    case 'sampling.sampleType':
      return (
        procedure.sampleType?.name?.[language] ?? procedure.sampleType?.code
      );
    case 'tests.category':
      return procedure.categoryOfTest;
    case 'tests.subcategory':
      return (
        getLocalizedName(procedure.subCategory?.name, language) ??
        procedure.subCategory?.code
      );
    case 'tests.family':
      return (
        getLocalizedName(procedure.family?.name, language) ??
        procedure.family?.code
      );
    case 'tests.extraInfo':
      return procedure.extraTestInformation;
    case 'results.positiveCount':
      return procedure.numberOfPositiveTests;
    case 'results.negativeCount':
      return procedure.numberOfNegativeTests;
    case 'results.totalCount':
      return procedure.numberOfTests;
    default:
      return undefined;
  }
};
